import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import InputAdornment from '@material-ui/core/InputAdornment'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import MUIVisibility from '@material-ui/icons/Visibility'
import MUIVisibilityOff from '@material-ui/icons/VisibilityOff'
import MUISchedule from '@material-ui/icons/Schedule'
import SearchIconUrl from 'src/assets/icons/search.svg'
import { CURRENCIES_OPTIONS } from 'src/features/offers/duck/consts'
import { withStyles } from '@material-ui/styles'
import CloseIcon from '@material-ui/icons/Close'
import Select from './InputUnitSelect'

const TextInput = ({
  label,
  name,
  id,
  value,
  onChange,
  validate,
  pristine,
  helperText,
  placeholder,
  variant,
  type,
  multiline,
  rows,
  required,
  unit,
  onCurrencyChange,
  currencyValue,
  disabled,
  optional,
  onBlur,
  hideError,
  inputRef // Add this prop
}) => {
  const classes = useStyles()
  const [passwordVisible, _setPasswordVisibility] = useState(false)
  const [inputPristine, _setPristine] = useState(pristine)
  const [{ valid, error }, _validate] = useState({ valid: true, errors: [] })

  const handleChange = e => onChange(name, e.target.value)

  useEffect(() => {
    if (!inputPristine || !pristine) {
      validate(name, value, v => {
        _validate(() => ({ valid: v.valid, error: v.errors.join(' ') }))
      })
    }
  }, [value, inputPristine, pristine])

  const handleClear = () => {
    onChange(name, '')
  }

  const endAdornmentIcon = () => {
    switch (type) {
      case 'password':
        return (
          <InputAdornment position='end'>
            <IconButton
              onClick={() => _setPasswordVisibility(!passwordVisible)}
            >
              {passwordVisible ? <MUIVisibility /> : <MUIVisibilityOff />}
            </IconButton>
          </InputAdornment>
        )
      case 'calendarTime':
        return (
          <InputAdornment position='end'>
            <MUISchedule />
          </InputAdornment>
        )
      case 'unit':
        return (
          <InputAdornment position='end'>
            {' '}
            <UnitIcon>{unit}</UnitIcon>
          </InputAdornment>
        )
      case 'search':
        return value ? (
          <InputAdornment position='end'>
            <IconButton
              className={classes.clearButton}
              disableRipple
              onClick={handleClear}
            >
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        ) : null
      case 'currency':
        return (
          <InputAdornment position='end'>
            <Select
              value={currencyValue}
              onChange={e => onCurrencyChange('currency', e.target.value)}
              onBlur={() => _setPristine(false)}
            >
              {CURRENCIES_OPTIONS.map(option => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </InputAdornment>
        )
      default:
        return null
    }
  }

  const startAdornmentIcon = () => {
    switch (type) {
      case 'search':
        return (
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        )
      default:
        return null
    }
  }

  return (
    <div className={classes.container}>
      <Label required={required}>
        {label}
        {optional && (
          <Optional>
            <FormattedMessage id='forms.optional' />
          </Optional>
        )}
      </Label>
      <DarkerDisabledTextField
        inputRef={inputRef} // Add this prop
        disabled={disabled}
        className={classes.textField}
        id={id}
        variant={variant || 'outlined'}
        name={name}
        value={value == null ? '' : value}
        onChange={handleChange}
        error={!valid}
        onBlur={() => {
          _setPristine(false)
          onBlur()
        }}
        type={!type || passwordVisible ? 'text' : type}
        multiline={multiline}
        rows={rows}
        placeholder={placeholder}
        InputLabelProps={{
          classes: {
            root: classes.label
          }
        }}
        InputProps={{
          classes: {
            root: classes.root,
            input: classes.input,
            inputMultiline: classes.inputMultiline,
            focused: classes.focused,
            notchedOutline: classes.notchedOutline
          },
          endAdornment: endAdornmentIcon(),
          startAdornment: startAdornmentIcon()
        }}
      />
      {!hideError && (
        <HelperText valid={valid}>{valid ? helperText : error}</HelperText>
      )}
    </div>
  )
}

TextInput.defaultProps = {
  label: '',
  helperText: '',
  id: '',
  name: '',
  value: '',
  pristine: true,
  multiline: false,
  rows: 4,
  handleChange: () => {},
  validate: () => true,
  onBlur: () => {}
}

export default TextInput

const DarkerDisabledTextField = withStyles({
  root: {
    marginRight: 8,
    '& .MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#f8f8f9'
    }
  }
})(TextField)

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    width: '100%'
  },
  textField: {
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(0),
    width: '100%'
  },
  root: {
    backgroundColor: 'white',
    '&$focused': {
      boxShadow: '0 2px 10px 0 rgba(19, 162, 191, 0.25)'
    },
    '& $notchedOutline': {
      borderWidth: '1px',
      borderColor: '#c6cbd4'
    },
    '&:hover $notchedOutline': {},
    '&$focused $notchedOutline': {
      borderWidth: '1px',
      borderColor: '#13a2bf'
    }
  },
  input: {
    padding: '10px 0 10px 12px',
    height: '20px',
    color: '#585a59',
    textOverflow: 'ellipsis'
  },
  inputMultiline: {
    height: 'auto',
    resize: 'none',
    padding: 0
  },
  label: {
    color: '#585a59',
    opacity: '0.7'
  },
  focused: {},
  notchedOutline: {
    borderRadius: '2px'
  },
  clearButton: {
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  underline: {},
  helperText: {
    color: '#373e4c',
    fontSize: '0.9rem',
    fontWeight: 500,
    lineHeight: 1.33,
    opacity: 0.87,
    marginBottom: '1rem'
  }
}))

const Label = styled.span`
  display: flex;
  width: 100%;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 0.3rem;
  margin-left: 0.2rem;
  ${({ required }) =>
    required &&
    `
    &:after {
      content: '*';
      color: red;
      font-size: 0.75rem;
    }
  `}
`

const UnitIcon = styled.span`
  display: inline-block;
  position: relative;
  color: ${({ theme }) => theme.colors.black.hex()};
  font-size: 0.875rem;
  padding-left: 0.6rem;
  &:before {
    content: '';
    width: 1px;
    height: 20px;
    background-color: ${({ theme }) => theme.colors.ghost.hex()};
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`

const ClearIcon = styled(CloseIcon)``

const HelperText = styled.span`
  display: inline-block;
  width: 100%;
  margin: 8px 6px 8px 0;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1em;
  color: ${({ theme, valid }) =>
    valid ? theme.colors.black.alpha(0.7).hex() : theme.colors.error.hex()};
`

const Optional = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black.alpha(0.7).hex()};
  margin-left: 0.2rem;
`

const SearchIcon = styled(SearchIconUrl)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.ebonyClay.alpha(0.3).hex()};
`
